import { Injectable, inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store, select } from '@ngrx/store'
import { from } from 'rxjs'
import { map, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { AccountsService } from 'src/services/data/accounts.service'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { catchAndLog } from 'src/shared/utils/catch-and-log'
import { waitNotEmpty } from 'src/shared/utils/wait-not-empty'
import { archiveAccount, createAccount, createAccountFailure, createAccountSuccess, disable2FAAccount, enable2FAAccount, loadAccounts, loadAccountsFailure, loadAccountsSuccess, reassignAccounts, reassignAccountsFailure, reassignAccountsSuccess, unarchiveAccount, updateAccount, updateAccountFailure, updateAccountStatus, updateAccountSuccess, updateAccountsBulk, updateAccountsBulkFailure, updateAccountsBulkSuccess } from './account.actions'
import { selectAccountEntities } from './account.selectors'


@Injectable()
export class AccountEffects {
  private actions$ = inject(Actions)
  private Accounts = inject(AccountsService)
  private store = inject(Store)
  private toaster = inject(ToasterService)


  loadAccounts$ = createEffect(() => this.actions$.pipe(
    ofType(loadAccounts),
    switchMap(action =>
      from(this.Accounts.getAccounts(action)).pipe(
        map(r => loadAccountsSuccess({ accounts: r.data, fields: action.fields as string[] })),
        catchAndLog(this.toaster, 'Unable to fetch accounts', loadAccountsFailure)))))

  createAccount$ = createEffect(() => this.actions$.pipe(
    ofType(createAccount),
    tap(() => this.toaster.progress('Creating account...')),
    switchMap(action =>
      from(this.Accounts.saveAccountImmutable(action.account)).pipe(
        map(account => createAccountSuccess({ account })),
        tap(() => this.toaster.success('Account created successfully.')),
        catchAndLog(this.toaster, 'Unable to create account', createAccountFailure)))))

  updateAccount$ = createEffect(() => this.actions$.pipe(
    ofType(updateAccount),
    tap(() => this.toaster.progress('Updating account...')),
    switchMap(action =>
      from(this.Accounts.patchAccountImmutable(action.account, action.patch)).pipe(
        map(account => updateAccountSuccess({ account })),
        tap(() => this.toaster.success('Account updated successfully.')),
        catchAndLog(this.toaster, 'Unable to update account', updateAccountFailure)))))

  updateAccountStatus$ = createEffect(() => this.actions$.pipe(
    ofType(updateAccountStatus),
    tap(() => this.toaster.progress('Updating account status...')),
    switchMap(action =>
      from(this.Accounts.patchAccountImmutable(action.account, { status: action.status })).pipe(
        map(account => updateAccountSuccess({ account })),
        tap(() => this.toaster.success('Account status updated successfully.')),
        catchAndLog(this.toaster, 'Unable to update account status', updateAccountFailure)))))

  updateAccountsBulk$ = createEffect(() => this.actions$.pipe(
    ofType(updateAccountsBulk),
    tap(() => this.toaster.progress('Updating accounts...')),
    switchMap(action =>
      from(Promise.all(action.patches.map(p =>
          this.Accounts.patchAccountImmutable({ account: p.account }, p.patch)))).pipe(
        map(accounts => updateAccountsBulkSuccess({ accounts })),
        tap(() => this.toaster.success('Accounts updated successfully.')),
        catchAndLog(this.toaster, 'Unable to update accounts', updateAccountsBulkFailure)))))

  reassignAccounts$ = createEffect(() => this.actions$.pipe(
    ofType(reassignAccounts),
    withLatestFrom(
      this.store.pipe(select(selectAccountEntities), waitNotEmpty())
    ),
    tap(() => this.toaster.progress('Reassigning accounts...')),
    switchMap(([action, accounts]) =>
      this.Accounts.reassignAccounts(accounts, action.coordinator_reassignments).pipe(
        map(accounts => reassignAccountsSuccess({ accounts })),
        tap(() => this.toaster.success('Reassigned accounts successfully')),
        catchAndLog(this.toaster, 'Unable to reassign accounts', reassignAccountsFailure)))))

  archiveAccount$ = createEffect(() => this.actions$.pipe(
    ofType(archiveAccount),
    tap(() => this.toaster.progress('Archiving account...')),
    switchMap(action =>
      from(this.Accounts.patchAccountImmutable({ account: action.account }, { archived: 1 })).pipe(
        map(account => updateAccountSuccess({ account })),
        tap(() => this.toaster.success('Account archived successfully.')),
        catchAndLog(this.toaster, 'Unable to update account', updateAccountFailure)))))

  unarchiveAccount$ = createEffect(() => this.actions$.pipe(
    ofType(unarchiveAccount),
    tap(() => this.toaster.progress('Unarchiving account...')),
    switchMap(action =>
      from(this.Accounts.patchAccountImmutable({ account: action.account }, { archived: 0 })).pipe(
        map(account => updateAccountSuccess({ account })),
        tap(() => this.toaster.success('Account unarchived successfully.')),
        catchAndLog(this.toaster, 'Unable to update account', updateAccountFailure)))))

  enable2FAAccount$ = createEffect(() => this.actions$.pipe(
    ofType(enable2FAAccount),
    tap(() => this.toaster.progress('Enabling 2FA for account...')),
    switchMap(action =>
      from(this.Accounts.patchAccountImmutable({ account: action.account }, { autocomplete_2fa: false })).pipe(
        map(account => updateAccountSuccess({ account })),
        tap(() => this.toaster.success('2FA enabled successfully.')),
        catchAndLog(this.toaster, 'Unable to update account', updateAccountFailure)))))

  disable2FAAccount$ = createEffect(() => this.actions$.pipe(
    ofType(disable2FAAccount),
    tap(() => this.toaster.progress('Disabling 2FA for account...')),
    switchMap(action =>
      from(this.Accounts.patchAccountImmutable({ account: action.account }, { autocomplete_2fa: true })).pipe(
        map(account => updateAccountSuccess({ account })),
        tap(() => this.toaster.success('2FA disabled successfully.')),
        catchAndLog(this.toaster, 'Unable to update account', updateAccountFailure)))))

}
